import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NotificationManager from "../../services/dashboard/NotificationManager";
import { Bell, Trash2, CheckCheck, Filter, Search } from "lucide-react";

export default function NotificationsCenterComponent() {
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL"); // ALL, UNREAD, READ
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // Fetch all notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await NotificationManager.getAllAdminNotifications();
      setNotifications(data);
      setFilteredNotifications(data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setNotifications([]);
      setFilteredNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...notifications];

    // Filter by read status
    if (filter === "UNREAD") {
      filtered = filtered.filter(n => !n.isRead);
    } else if (filter === "READ") {
      filtered = filtered.filter(n => n.isRead);
    }

    // Filter by type
    if (typeFilter !== "ALL") {
      filtered = filtered.filter(n => n.type === typeFilter);
    }

    // Search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(n => 
        n.title.toLowerCase().includes(searchLower) ||
        n.message?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredNotifications(filtered);
  }, [filter, typeFilter, searchTerm, notifications]);

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.isRead) {
        await NotificationManager.markAsRead(notification.notificationId);
      }
      
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
      }
      
      fetchNotifications();
    } catch (error) {
      console.error("Error handling notification click:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await NotificationManager.markAllAsRead();
      fetchNotifications();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    if (window.confirm("Delete this notification?")) {
      try {
        await NotificationManager.deleteNotification(notificationId);
        fetchNotifications();
      } catch (error) {
        console.error("Error deleting notification:", error);
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Just now";
    
    try {
      // Handle array format [year, month, day, hour, minute, second]
      let date;
      if (Array.isArray(timestamp)) {
        const [year, month, day, hour = 0, minute = 0, second = 0] = timestamp;
        date = new Date(year, month - 1, day, hour, minute, second);
      } else {
        date = new Date(timestamp);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) return "Just now";
      
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      
      const diffMonths = Math.floor(diffDays / 30);
      if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
      
      const diffYears = Math.floor(diffDays / 365);
      return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return "Just now";
    }
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      NEW_ORDER: "📦",
      NEW_USER: "👤",
      NEW_SUBSCRIBER: "⭐",
      BOOK_SUGGESTION: "💡",
      RETURN_REQUEST: "↩️",
      BOOK_DONATE: "📚",
      INVESTOR_ENQUIRY: "💼",
      PAYMENT_RECEIVED: "💰",
      BOOK_ENQUIRY: "❓",
      SUBSCRIPTION_EXPIRING: "⏰",
    };
    return iconMap[type] || "🔔";
  };

  const getPriorityColor = (priority) => {
    const colorMap = {
      URGENT: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-300 dark:border-red-800",
      HIGH: "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-800",
      NORMAL: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-800",
      LOW: "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-700",
    };
    return colorMap[priority] || colorMap.NORMAL;
  };

  const notificationTypes = [
    { value: "ALL", label: "All Types" },
    { value: "NEW_ORDER", label: "Orders" },
    { value: "NEW_USER", label: "New Users" },
    { value: "NEW_SUBSCRIBER", label: "Subscriptions" },
    { value: "PAYMENT_RECEIVED", label: "Payments" },
    { value: "BOOK_SUGGESTION", label: "Book Suggestions" },
    { value: "RETURN_REQUEST", label: "Return Requests" },
    { value: "BOOK_DONATE", label: "Donations" },
    { value: "INVESTOR_ENQUIRY", label: "Investor Enquiries" },
    { value: "BOOK_ENQUIRY", label: "Book Enquiries" },
    { value: "SUBSCRIPTION_EXPIRING", label: "Expiring Soon" },
  ];

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <Bell className="w-7 h-7" />
                Notifications Center
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <CheckCheck className="w-4 h-4" />
                Mark All as Read
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Status Filter */}
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="ALL">All Notifications</option>
                <option value="UNREAD">Unread Only</option>
                <option value="READ">Read Only</option>
              </select>

              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {notificationTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-12 text-center">
            <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {searchTerm || filter !== "ALL" || typeFilter !== "ALL" 
                ? "No notifications found" 
                : "No notifications yet"}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || filter !== "ALL" || typeFilter !== "ALL"
                ? "Try adjusting your filters"
                : "You'll see notifications here as events occur"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.notificationId}
                onClick={() => handleNotificationClick(notification)}
                className={`bg-white dark:bg-gray-900 rounded-xl border ${
                  notification.isRead 
                    ? "border-gray-200 dark:border-gray-800" 
                    : "border-orange-300 dark:border-orange-900 ring-2 ring-orange-100 dark:ring-orange-900/30"
                } p-4 hover:shadow-lg transition-all cursor-pointer group`}
              >
                <div className="flex gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-900/30 dark:to-orange-800/30 flex items-center justify-center text-2xl">
                      {getNotificationIcon(notification.type)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1">
                        <h3 className={`text-base font-semibold ${
                          notification.isRead 
                            ? "text-gray-700 dark:text-gray-300" 
                            : "text-gray-900 dark:text-white"
                        }`}>
                          {notification.title}
                        </h3>
                        {!notification.isRead && (
                          <span className="inline-block w-2 h-2 bg-orange-500 rounded-full ml-2"></span>
                        )}
                      </div>
                      
                      {/* Priority Badge */}
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(notification.priority)}`}>
                        {notification.priority}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {notification.message}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 dark:text-gray-500">
                        {formatTimestamp(notification.createdAt)}
                      </span>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => handleDelete(e, notification.notificationId)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                        title="Delete notification"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
